import "@popperjs/core";
import { Fragment, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MenuComponent } from "src/resources/assets/ts/components";
import { toAbsoluteUrl } from "src/resources/helpers/AssetHelpers";
import * as lc from "src/app/modules/localstorage/index";
import ContentContainer from "src/app/layouts/content/ContentContainer";
import { PageDataProvider } from "src/app/layout/core";
import ApplicationHeader from "src/app/layouts/header";
import ApplicationFooter from "src/app/layouts/footer";
import { useTheme } from "src/setup/theme/useTheme";
import { useTheme as useAppTheme } from "src/context/ThemeContext";
import {
  NewSidebar,
  useNewSidebar,
  SidebarItem,
  BalanceWidget,
} from "src/app/layouts/sidebar";
import { BottomNavigation } from "src/app/layouts/sidebar/components";
import {
  createBottomNavigationData,
  createSettingsNavigationData,
} from "src/app/layouts/sidebar/utils/navigationMapper";
// Import old layout components for legacy pages
import { CustomHeader } from "src/app/layout/header/CustomHeader";
import { Toolbar } from "src/app/layout/toolbar/Toolbar";
import { Content } from "src/app/layout/Content";
// Import context and modals for logout flow
import { UnsavedChangesProvider } from "src/contexts/UnsavedChangesContext";
import {
  UnsavedChangesModal,
  LogoutConfirmationModal,
} from "src/shared/components";
import { HeaderVisibilityProvider, useHeaderVisibility } from "../core/HeaderVisibilityContext";

const MasterLayout = () => {
  return (
    <UnsavedChangesProvider>
      <HeaderVisibilityProvider>
        <MasterLayoutContent />
      </HeaderVisibilityProvider>
    </UnsavedChangesProvider>
  );
};

const MasterLayoutContent = () => {
  const location = useLocation();
  const currentTheme = useTheme();
  const { theme: appTheme } = useAppTheme();
  const nav = useNavigate();

  // New sidebar implementation
  const newSidebarData = useNewSidebar();
  const newContentRoute = [
    "/product/listing",
    "/product/merchant",
    "/product/merchant/create",
    "/product/merchant/edit",
  ];
  const isNewContentRoute = newContentRoute.some((route) =>
    location.pathname.startsWith(route)
  );

  // Wizard page detection - pages that use their own fixed navigation
  // const wizardRoutes = ["/product/merchant/create", "/product/merchant/edit"];
  // const isWizardPage = wizardRoutes.some((route) =>
  //   location.pathname.startsWith(route)
  // );

  // Create bottom navigation data with translation function
  const { t } = useTranslation();

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [activeParentId, setActiveParentId] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [manuallyExpanded, setManuallyExpanded] = useState<string[]>([]);
  const [currentLevel, setCurrentLevel] = useState<"main" | "settings">("main");
  const [navigationHistory, setNavigationHistory] = useState<string[]>([
    "main",
  ]);

  // Responsive detection
  const isDesktopOrLaptop = useMediaQuery({
    query: "(min-width: 900px)",
  });

  // Component-based header visibility control via context
  const { shouldHideHeader: hideHeaderFromContext } = useHeaderVisibility();
  const shouldHideHeader = !isDesktopOrLaptop && hideHeaderFromContext;

  // Auto-hide header on scroll (mobile only)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  const bottomNavigationItems = createBottomNavigationData(
    newSidebarData.totalUnreadHandled,
    newSidebarData.totalUnreadUnhandled,
    t
  );

  // Update active item based on current route
  useEffect(() => {
    // Only auto-switch based on route if user is on desktop or if sidebar is not manually opened
    // Don't interfere with manual mobile sidebar navigation
    const isDesktop = window.innerWidth >= 900;

    if (isDesktop) {
      // Desktop behavior - auto-switch based on route
      if (location.pathname.startsWith("/setting")) {
        if (currentLevel !== "settings") {
          setCurrentLevel("settings");
          setNavigationHistory(["main", "settings"]);
        }
      } else {
        if (currentLevel !== "main") {
          setCurrentLevel("main");
          setNavigationHistory(["main"]);
        }
      }
    }
    // Mobile behavior - don't auto-switch, let user control the sidebar navigation manually

    const findActiveItem = (
      items: any[],
      parentId: string | null = null
    ): { activeId: string | null; parentId: string | null } => {
      for (const item of items) {
        if (item.path && item.path !== "#") {
          if (location.pathname === item.path) {
            return { activeId: item.id, parentId };
          }
          if (
            item.path.length > 1 &&
            location.pathname.startsWith(item.path + "/")
          ) {
            return { activeId: item.id, parentId };
          }
        }

        if (item.children && item.children.length > 0) {
          const childResult = findActiveItem(item.children, item.id);
          if (childResult.activeId) {
            return childResult;
          }
        }
      }
      return { activeId: null, parentId: null };
    };

    // Check both main navigation and settings navigation for active items
    let result = findActiveItem(newSidebarData.navigationItems);

    // If no result found in main navigation and we're on a settings route, check settings navigation
    if (!result.activeId && location.pathname.startsWith("/setting")) {
      const settingsResult = findActiveItem(createSettingsNavigationData(t));
      if (settingsResult.activeId) {
        result = settingsResult;
      }
    }

    setActiveItemId(result.activeId);
    setActiveParentId(result.parentId);

    // Combine automatic expansion (for active routes) with manual expansion
    const autoExpanded = result.parentId ? [result.parentId] : [];
    setExpandedItems((prev) => {
      // Merge auto-expanded and manually expanded, removing duplicates
      const combined = [...new Set([...autoExpanded, ...manuallyExpanded])];
      return combined;
    });
  }, [location.pathname, manuallyExpanded, currentLevel]); // Include manuallyExpanded and currentLevel dependencies

  // Handle back navigation in mobile settings panel
  const handleMobileBackNavigation = () => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current level
      const previousLevel = newHistory[newHistory.length - 1];
      setNavigationHistory(newHistory);
      setCurrentLevel(previousLevel as "main" | "settings");
    }
  };

  // this useEffect is to check if user is logged in, if not redirect to /auth
  useEffect(() => {
    document.body.classList.add("tw-font-display");
    const user = lc.getItemLC(lc.LCName.User);
    if (!user) {
      nav("/auth");
    }
    setTimeout(() => {
      MenuComponent.reinitialization();
    }, 500);
    return () => {
      document.body.classList.remove("tw-bg-background-white");
      document.body.classList.remove("tw-font-display");
      document.body.classList.remove("tw-bg-background-light");
      document.body.classList.remove("tw-bg-background-dark");
      document.documentElement.classList.remove("tw-bg-background-light");
      document.documentElement.classList.remove("tw-bg-background-dark");
    };
  }, [nav]);

  // Theme integration - apply theme-aware background classes
  useEffect(() => {
    // Remove existing background classes
    document.body.classList.remove(
      "tw-bg-background-light",
      "tw-bg-background-dark"
    );
    document.documentElement.classList.remove(
      "tw-bg-background-light",
      "tw-bg-background-dark"
    );

    // Apply theme-aware classes
    const bgClass =
      appTheme === "dark" ? "tw-bg-background-dark" : "tw-bg-background-light";
    document.body.classList.add(bgClass);
    document.documentElement.classList.add(bgClass);

    console.log("MasterLayout: Applied theme background class:", bgClass); // Debug log
  }, [appTheme]);

  // this useEffect is to reinitialize the menu component on location change, with 500ms delay
  useEffect(() => {
    setTimeout(() => {
      MenuComponent.reinitialization();
    }, 500);
  }, [location.key]);

  // Auto-hide header on scroll (mobile only)
  useEffect(() => {
    if (isDesktopOrLaptop || !contentScrollRef.current) return; // Only for mobile

    const scrollElement = contentScrollRef.current;

    const handleScroll = () => {
      const currentScrollY = scrollElement.scrollTop;
      const scrollDelta = currentScrollY - lastScrollYRef.current;

      // Show header when scrolling up or at top
      if (scrollDelta < 0 || currentScrollY < 10) {
        setIsHeaderVisible(true);
      }
      // Hide header when scrolling down (after 10px threshold)
      else if (scrollDelta > 0 && currentScrollY > 60) {
        setIsHeaderVisible(false);
      }

      lastScrollYRef.current = currentScrollY;
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [isDesktopOrLaptop]);

  const LayoutWeb = () => (
    <PageDataProvider>
      <ApplicationHeader />
      <NewSidebar
        isMinimized={newSidebarData.isMinimized}
        navigationItems={newSidebarData.navigationItems}
        balance={newSidebarData.balance}
        onToggleMinimize={newSidebarData.toggleMinimize}
      />
      <div className="tw-flex tw-h-screen tw-flex-col tw-pt-16 tw-transition-margin tw-duration-300 tw-ease-in-out main-content tw-overflow-hidden">
        {isNewContentRoute ? (
          <ContentContainer>
            <Outlet />
          </ContentContainer>
        ) : (
          <div className="d-flex flex-column flex-grow-1 bg-light" style={{ minHeight: 0, overflow: "hidden" }}>
            <Toolbar />
            <Content>
              <Outlet />
            </Content>
          </div>
        )}

        <ApplicationFooter />
      </div>

      {/* Logout Flow Modals */}
      <UnsavedChangesModal
        isOpen={newSidebarData.showUnsavedChangesModal}
        onCancel={newSidebarData.handleUnsavedChangesCancel}
        onDiscard={newSidebarData.handleUnsavedChangesDiscard}
        title="Unsaved Changes"
        message="You have unsaved changes that will be lost if you logout. Are you sure you want to continue?"
      />

      <LogoutConfirmationModal
        isOpen={newSidebarData.showLogoutConfirmationModal}
        onCancel={newSidebarData.handleLogoutConfirmationCancel}
        onConfirm={newSidebarData.handleLogoutConfirmationConfirm}
      />
    </PageDataProvider>
  );

  const LayoutMobile = () => (
    <Fragment>
      <div className="tw-fixed tw-inset-0 tw-bg-background-light dark:tw-bg-background-dark tw-text-slate-800 dark:tw-text-slate-200 tw-overflow-hidden tw-flex tw-flex-col">
        {/* Fixed Mobile Header - auto-hides on scroll, hidden completely on chat pages */}
        {!shouldHideHeader && (
          <header
            className={`tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-z-[120] tw-bg-white dark:tw-bg-background-dark tw-shadow-sm tw-border-b tw-transition-transform tw-duration-300 ${
              appTheme === "light" ? "tw-border-gray-200" : "tw-border-slate-600"
            }`}
            style={{
              transform: isHeaderVisible ? 'translateY(0)' : 'translateY(-100%)'
            }}
          >
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-1.5">
              <div className="tw-flex tw-items-center tw-gap-2">
                <img
                  src={toAbsoluteUrl("/media/logos/logo-sevendays-with-text.svg")}
                  alt="SevenDays Logo"
                  className={`tw-h-7 tw-w-auto ${
                    appTheme === "dark" ? "tw-brightness-0 tw-invert" : ""
                  }`}
                />
              </div>
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="tw-p-2 -tw-mr-2 tw-text-slate-500 hover:tw-text-slate-700 dark:tw-text-slate-400 dark:hover:tw-text-white tw-transition-all tw-duration-200 hover:tw-scale-110 active:tw-scale-95"
              >
                <span className="material-symbols-outlined tw-text-2xl">
                  menu
                </span>
              </button>
            </div>
          </header>
        )}

        {/* Content Area - no page scroll, flex container for chat */}
        <div
          ref={contentScrollRef}
          className={`tw-flex-1 tw-overflow-hidden ${shouldHideHeader ? 'tw-pt-0' : 'tw-pt-[40px]'} tw-pb-0 ${
            isMobileSidebarOpen ? "tw-pointer-events-none" : ""
          }`}
        >
          <main className="tw-flex tw-flex-col tw-h-full">
            {isNewContentRoute ? (
              <ContentContainer>
                <Outlet />
              </ContentContainer>
            ) : (
              // Use old layout structure for legacy pages
              <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-overflow-hidden tw-bg-light">
                <Toolbar />
                <Content>
                  <Outlet />
                </Content>
              </div>
            )}
          </main>
        </div>

        {/* Mobile Sidebar Overlay with fade animation */}
        <div
          className={`tw-fixed tw-inset-0 tw-z-[125] tw-bg-black/40 tw-backdrop-blur-sm tw-transition-opacity tw-duration-300 tw-ease-in-out ${
            isMobileSidebarOpen
              ? "tw-opacity-100 tw-pointer-events-auto"
              : "tw-opacity-0 tw-pointer-events-none"
          }`}
          onClick={() => setIsMobileSidebarOpen(false)}
        />

        {/* Mobile Sidebar with slide animation */}
        <div
          className={`tw-fixed tw-top-0 tw-left-0 tw-bottom-0 tw-z-[130] tw-flex tw-w-[80%] tw-max-w-[320px] tw-flex-col tw-bg-white dark:tw-bg-brand-dark tw-shadow-2xl tw-transform tw-transition-transform tw-duration-300 tw-ease-in-out ${
            isMobileSidebarOpen ? "tw-translate-x-0" : "-tw-translate-x-full"
          }`}
        >
          <div
            className={`tw-flex tw-items-center tw-justify-between tw-border-b ${
              appTheme === "light"
                ? "tw-border-gray-200"
                : "tw-border-slate-600"
            } tw-px-4 tw-py-3`}
          >
            <img
              src={toAbsoluteUrl("/media/logos/logo-sevendays-with-text.svg")}
              alt="SevenDays Logo"
              className={`tw-h-10 tw-w-auto ${
                appTheme === "dark" ? "tw-brightness-0 tw-invert" : ""
              }`}
            />
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className="tw-p-2 -tw-mr-2 tw-text-slate-500 hover:tw-text-slate-700 dark:tw-text-slate-400 dark:hover:tw-text-white tw-transition-all tw-duration-200 hover:tw-scale-110 active:tw-scale-95"
            >
              <span className="material-symbols-outlined tw-text-3xl">
                close
              </span>
            </button>
          </div>

          <div className="tw-flex-1 tw-overflow-hidden tw-pt-4">
            {/* Sliding container for navigation levels - matching desktop implementation */}
            <div
              className="tw-flex tw-h-full tw-transition-transform tw-duration-300 tw-ease-in-out tw-overflow-x-hidden"
              style={{
                transform: `translateX(${
                  currentLevel === "main" ? "0%" : "-50%"
                })`,
                width: "200%",
              }}
            >
              {/* Main navigation panel */}
              <div className="tw-flex tw-w-1/2 tw-flex-col tw-h-full">
                <div className="tw-flex-1 tw-overflow-y-auto tw-no-scrollbar">
                  <nav className="tw-flex tw-flex-col tw-gap-2 tw-px-3">
                    {/* Main navigation items */}
                    {newSidebarData.navigationItems
                      .filter(
                        (item) =>
                          !["help", "logout", "settings"].includes(item.id)
                      )
                      .map((item) => (
                        <SidebarItem
                          key={item.id}
                          item={{
                            ...item,
                            isActive:
                              activeItemId === item.id ||
                              activeParentId === item.id,
                          }}
                          isMinimized={false}
                          onItemClick={(clickedItem) => {
                            // If item has children, toggle manual expansion instead of closing sidebar
                            if (
                              clickedItem.children &&
                              clickedItem.children.length > 0
                            ) {
                              setManuallyExpanded((prev) => {
                                const newManualExpanded = prev.includes(
                                  clickedItem.id
                                )
                                  ? prev.filter((id) => id !== clickedItem.id)
                                  : [...prev, clickedItem.id];
                                return newManualExpanded;
                              });
                            } else {
                              // Only close sidebar for items without children (leaf items)
                              if (
                                clickedItem.path &&
                                clickedItem.path !== "#"
                              ) {
                                setIsMobileSidebarOpen(false);
                              }
                            }
                          }}
                          isExpanded={expandedItems.includes(item.id)}
                          activeItemId={activeItemId}
                          activeParentId={activeParentId}
                          depth={0}
                          expandedItems={expandedItems}
                        />
                      ))}
                  </nav>
                </div>

                {/* Bottom section with balance widget and settings/logout - matching HTML design */}
                <div className="tw-px-3 tw-py-4 tw-mt-auto">
                  {/* Balance Widget */}
                  {newSidebarData.balance && (
                    <BalanceWidget
                      balance={newSidebarData.balance}
                      isMinimized={false}
                    />
                  )}

                  {/* Settings and Logout navigation - matching HTML design */}
                  <nav className="tw-flex tw-flex-col tw-gap-2 tw-pt-2">
                    {/* Settings button */}
                    <button
                      onClick={() => {
                        setCurrentLevel("settings");
                        setNavigationHistory(["main", "settings"]);
                      }}
                      className={`tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-px-2 tw-py-1.5 tw-text-sm tw-font-medium tw-transition-colors tw-duration-200 ${
                        location.pathname.startsWith("/setting")
                          ? appTheme === "light"
                            ? "tw-bg-[#EBF5FF] tw-text-[#007AFF]"
                            : "tw-bg-blue-600 tw-text-white"
                          : appTheme === "light"
                          ? "tw-text-slate-600 hover:tw-bg-slate-100"
                          : "!tw-text-slate-50 hover:tw-bg-slate-600"
                      }`}
                    >
                      <span className="material-symbols-outlined tw-text-xl">
                        settings
                      </span>
                      <span className="tw-flex-1 tw-text-left">
                        {t("SideBar.MenuItem.Settings")}
                      </span>
                    </button>
                    {newSidebarData.navigationItems
                      .filter((item) => ["logout"].includes(item.id))
                      .map((item) => (
                        <SidebarItem
                          key={item.id}
                          item={{
                            ...item,
                            isActive: activeItemId === item.id,
                          }}
                          isMinimized={false}
                          onItemClick={(clickedItem) => {
                            if (clickedItem.onClick) clickedItem.onClick();
                            setIsMobileSidebarOpen(false);
                          }}
                          isExpanded={expandedItems.includes(item.id)}
                          activeItemId={activeItemId}
                          activeParentId={activeParentId}
                          depth={0}
                          expandedItems={expandedItems}
                        />
                      ))}
                  </nav>
                </div>
              </div>

              {/* Settings navigation panel */}
              <div className="tw-flex tw-w-1/2 tw-flex-col tw-h-full">
                <div className="tw-flex-1 tw-overflow-y-auto tw-no-scrollbar">
                  {/* Settings header with back button - matching desktop design */}
                  <div className="tw-px-3 tw-mb-4">
                    <div
                      className={`tw-flex tw-items-center tw-gap-3 tw-px-2 tw-border-b ${
                        appTheme === "light"
                          ? "tw-border-gray-200"
                          : "tw-border-slate-600"
                      } tw-pb-3 tw-cursor-pointer`}
                      onClick={handleMobileBackNavigation}
                    >
                      <button
                        className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-gray-50 dark:tw-bg-slate-700 hover:tw-bg-gray-100 dark:hover:tw-bg-slate-600 tw-transition-all tw-duration-200 hover:tw-shadow-sm"
                        title="Back to main menu"
                      >
                        <span className="material-symbols-outlined tw-text-lg tw-text-gray-600 dark:tw-text-slate-300">
                          chevron_left
                        </span>
                      </button>
                      <div className="tw-flex tw-flex-col">
                        <h2 className="tw-text-sm tw-font-semibold tw-text-gray-900 dark:tw-text-white">
                          {t("SideBar.MenuItem.Settings")}
                        </h2>
                      </div>
                    </div>
                  </div>

                  <nav className="tw-flex tw-flex-col tw-gap-2 tw-px-3">
                    {/* Settings navigation items */}
                    {createSettingsNavigationData(t).map((item) => (
                      <SidebarItem
                        key={item.id}
                        item={{
                          ...item,
                          isActive:
                            activeItemId === item.id ||
                            activeParentId === item.id ||
                            (item.path &&
                              item.path !== "#" &&
                              location.pathname.startsWith(item.path)),
                        }}
                        isMinimized={false}
                        onItemClick={(clickedItem) => {
                          if (
                            clickedItem.children &&
                            clickedItem.children.length > 0
                          ) {
                            // Toggle expansion for items with children
                            setExpandedItems((prev) =>
                              prev.includes(clickedItem.id)
                                ? prev.filter((id) => id !== clickedItem.id)
                                : [...prev, clickedItem.id]
                            );
                          } else {
                            // Navigate for leaf items and close sidebar
                            if (clickedItem.path && clickedItem.path !== "#") {
                              setIsMobileSidebarOpen(false);
                            }
                          }
                        }}
                        isExpanded={expandedItems.includes(item.id)}
                        activeItemId={activeItemId}
                        activeParentId={activeParentId}
                        depth={0}
                        expandedItems={expandedItems}
                      />
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Navigation */}
        {/* <BottomNavigation
          items={bottomNavigationItems}
          onSettingsClick={() => {
            setIsMobileSidebarOpen(true);
            setCurrentLevel("settings");
            setNavigationHistory(["main", "settings"]);
          }}
        /> */}
      </div>

      {/* Logout Flow Modals */}
      <UnsavedChangesModal
        isOpen={newSidebarData.showUnsavedChangesModal}
        onCancel={newSidebarData.handleUnsavedChangesCancel}
        onDiscard={newSidebarData.handleUnsavedChangesDiscard}
        title="Unsaved Changes"
        message="You have unsaved changes that will be lost if you logout. Are you sure you want to continue?"
      />

      <LogoutConfirmationModal
        isOpen={newSidebarData.showLogoutConfirmationModal}
        onCancel={newSidebarData.handleLogoutConfirmationCancel}
        onConfirm={newSidebarData.handleLogoutConfirmationConfirm}
      />
    </Fragment>
  );

  const layout = isDesktopOrLaptop ? LayoutWeb() : LayoutMobile();

  return layout;
};

export default MasterLayout;
